import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2, X, Heart, Sparkles, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAID_TIERS = ["pro", "platinum", "ultimate"];
const PRESETS = [5, 25, 100, 250, 500];

export const TipCreatorModal = ({ post, onClose, onSent }) => {
  const { token, user, refreshUser } = useAuth();
  const [amount, setAmount] = useState(25);
  const [sending, setSending] = useState(false);

  const tier = (user?.subscription_tier || "free").toLowerCase();
  const isPaid = PAID_TIERS.includes(tier);
  const userTokens = user?.tokens ?? 0;
  const canAfford = userTokens >= amount;
  const isOwn = post?.user_id === user?.id;

  const submit = async () => {
    if (!isPaid || isOwn || !canAfford) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/tips/send`,
        { post_id: post.id, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent ${amount} XEA to ${post.user_name}! 🎉`);
      refreshUser?.();
      onSent?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tip failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-950/70 backdrop-blur-sm p-4"
      onClick={() => !sending && onClose()}
      data-testid="tip-modal"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-coral-500/20 rounded-full blur-3xl pointer-events-none" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-ink-100 rounded-full text-ink-500"
          data-testid="tip-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full mb-3">
            <Sparkles className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
              Weekend Top 20
            </span>
          </div>
          <h3 className="text-2xl font-black text-ink-950 mb-1">
            Tip {post?.user_name}
          </h3>
          <p className="text-sm text-ink-500 mb-5">
            Send XEA tokens directly. 100% goes to the creator&apos;s earnings — they
            can withdraw to their balance anytime.
          </p>

          {/* Restriction banners */}
          {!isPaid && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4 flex gap-2"
              data-testid="tip-tier-gate"
            >
              <Lock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-900">Pro tier required</p>
                <p className="text-xs text-amber-800">
                  Tipping is a Pro / Platinum / Ultimate perk. Upgrade your plan to send tips.
                </p>
              </div>
            </div>
          )}
          {isPaid && isOwn && (
            <div className="bg-ink-100 rounded-2xl p-3 mb-4 text-sm text-ink-700">
              You can&apos;t tip your own post.
            </div>
          )}

          {/* Amount presets */}
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">
            Amount (5 – 500 XEA)
          </p>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {PRESETS.map((n) => (
              <button
                key={n}
                onClick={() => setAmount(n)}
                disabled={!isPaid || isOwn || sending}
                data-testid={`tip-preset-${n}`}
                className={`py-2 rounded-xl text-sm font-bold transition-all border ${
                  amount === n
                    ? "bg-ink-950 text-white border-ink-950"
                    : "bg-white text-ink-950 border-ink-200 hover:border-ink-950"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {n}
              </button>
            ))}
          </div>

          <input
            type="range"
            min="5"
            max="500"
            step="5"
            value={amount}
            onChange={(e) => setAmount(parseInt(e.target.value, 10))}
            disabled={!isPaid || isOwn || sending}
            data-testid="tip-amount-slider"
            className="w-full accent-coral-500"
          />
          <div className="flex items-end justify-between mb-5 mt-1">
            <span className="text-[10px] text-ink-500">5</span>
            <span className="text-3xl font-black text-ink-950 leading-none">
              {amount} <span className="text-sm font-bold text-ink-500">XEA</span>
            </span>
            <span className="text-[10px] text-ink-500">500</span>
          </div>

          {/* Sender balance / affordability */}
          <div className="flex items-center justify-between text-xs mb-5 px-3 py-2 bg-ink-50 rounded-xl">
            <span className="text-ink-500">Your balance</span>
            <span className={`font-bold ${canAfford ? "text-ink-950" : "text-rose-600"}`}>
              {userTokens.toLocaleString()} XEA
            </span>
          </div>

          {isPaid && !canAfford && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 mb-4 text-xs text-rose-700">
              Not enough tokens. Top up before tipping.
            </div>
          )}

          <Button
            onClick={submit}
            disabled={!isPaid || isOwn || !canAfford || sending}
            data-testid="tip-submit"
            className="w-full bg-coral-500 hover:bg-coral-600 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
            Send {amount} XEA
            <Coins className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TipCreatorModal;
